import { Button } from '@ipfabric/ui-components/Button/Button';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import {
  type TableBulkActionProps,
  useTableContextSelectors,
} from 'components/Table/model/TableModel';
import { deselectAllTableRows } from 'components/Table/model/commands/deselectAllTableRows';
import { useState } from 'react';
import type { FC } from 'react';

interface Props {
  className?: string;
}

type BulkActionProps = {
  bulkAction: TableBulkActionProps;
};

const BulkActionButton: FC<BulkActionProps> = (props) => {
  const ctxProps = useTableContextSelectors({
    bulkActions: prop('bulkActions'),
    dispatch: prop('dispatch'),
    selectedRows: prop('selectedRows'),
    data: prop('data'),
    rowIdField: prop('rowIdField'),
  });

  const getSelectedData = () => {
    const selectedRowIds = Array.from(ctxProps.selectedRows);
    if (!ctxProps.rowIdField || !selectedRowIds) {
      return [];
    }

    return ctxProps.data.filter((x) =>
      Array.from(ctxProps.selectedRows).includes(
        (x as any)[ctxProps.rowIdField!],
      ),
    );
  };

  const [loading, setLoading] = useState<boolean>(false);

  const button =
    'onClick' in props.bulkAction ? (
      <Button
        variant='outline'
        type='button'
        accent={props.bulkAction.accent}
        icon={props.bulkAction.icon}
        iconStyle={props.bulkAction.iconStyle}
        disabled={props.bulkAction.disabled}
        loading={loading}
        onClick={async () => {
          if (!('onClick' in props.bulkAction)) {
            return;
          }

          await props.bulkAction.onClick({
            selectedRows: ctxProps.selectedRows,
            selectedData: getSelectedData(),
            deselectAll: () => {
              ctxProps.dispatch(deselectAllTableRows);
            },
            indicateRunningAction: async (callback) => {
              try {
                setLoading(true);
                await callback();
              } finally {
                setLoading(false);
              }
            },
          });
          if (!props.bulkAction.disableAutoDeselect) {
            ctxProps.dispatch(deselectAllTableRows);
          }
        }}
      >
        {props.bulkAction.title}
      </Button>
    ) : (
      <Button
        renderAs='anchor'
        variant='outline'
        accent={props.bulkAction.accent}
        icon={props.bulkAction.icon}
        iconStyle={props.bulkAction.iconStyle}
        disabled={!!props.bulkAction.disabled}
        loading={loading}
        href={props.bulkAction.href}
      >
        {props.bulkAction.title}
      </Button>
    );

  return props.bulkAction.disabled &&
    props.bulkAction.disabledReason !== undefined ? (
    <Tooltip content={props.bulkAction.disabledReason}>
      <div>{button}</div>
    </Tooltip>
  ) : (
    button
  );
};

export const TableBulkActions: FC<Props> = (props) => {
  const ctxProps = useTableContextSelectors({
    bulkActions: prop('bulkActions'),
    selectedRows: prop('selectedRows'),
  });

  return (
    <div className={classNames('flex flex-row gap-2', props.className)}>
      {ctxProps.bulkActions?.map((bulkAction) => {
        const bulkActionProps =
          typeof bulkAction === 'function'
            ? bulkAction({
                selectedRows: ctxProps.selectedRows,
              })
            : bulkAction;

        return (
          <BulkActionButton
            key={bulkActionProps.key}
            bulkAction={bulkActionProps}
          />
        );
      })}
    </div>
  );
};
